import React from 'react';
import * as Yup from 'yup';
import { isAxiosError } from 'axios';
import { Box, Collapse, LinearProgress, Paper, Typography } from '@mui/material';
import { InsertDriveFileOutlined } from '@mui/icons-material';
import { Formik } from 'formik';
import { Alert, AlertInline, Button, CancelButton, Modal, ModalActions, ModalContent, ModalTitle, Select, SubmitButton, TextInput, useAlert } from '@uplift/ui-v2';
import { UploadedProviderDocument, UploadedProviderDocumentType, UploadPurpose, createProviderDocument, uploadedProviderDocumentTypeLabels, uploadFileToS3 } from '@uplift/api';
import { useUpdateUploadedProviderDocumentMutation } from '@uplift/queries';
import { useL10n, useTimeoutResettableState } from '@uplift/hooks';


const documentTypeOptions = Object.values(UploadedProviderDocumentType).map(value => ({
  value,
  label: uploadedProviderDocumentTypeLabels[value],
}));

const validationSchema = Yup.object().shape({
  documentType: Yup.string().nullable().required('A document label is required'),
  description: Yup.string().max(256).required('A document description is required'),
});


export type ProviderDocumentDetailsFormProps = {
  providerId: string;
  file: File;
  onCancel: () => void;
  onSuccess: () => Promise<void>;
};


const ProviderDocumentDetailsFields: React.VFC = () => {
  const [ warningShown, setWarningShown ] = useTimeoutResettableState(5000, false);

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Box position="absolute" left="0" top="0" width="100%" display="flex" alignItems="center" justifyContent="center" zIndex="2">
        <Collapse in={warningShown}>
          <Alert severity="info" icon={null}>Maximum character count reached</Alert>
        </Collapse>
      </Box>
      <Box width="345px">
        <Select
          color="secondary"
          name="documentType"
          label="What is this document?"
          items={documentTypeOptions}
        />
      </Box>
      <TextInput
        maxCharacters={256}
        onCharactersLimit={() => setWarningShown(true)}
        name="description"
        color="secondary"
        label="Document description"
        fullWidth
        multiline
        minRows={2}
        maxRows={3}
        helperText="Use this space to add details that could help you find this later"
        mixHelperTextAndError
      />
    </Box>
  );
};


export const ProviderDocumentDetailsForm: React.VFC<ProviderDocumentDetailsFormProps> = (props) => {
  return (
    <Formik
      onSubmit={async (values, helpers) => {
        if (!values.documentType) return;
        if (!values.description) return;

        try {
          const uploadReq = await uploadFileToS3({
            providerId: props.providerId,
            purpose: UploadPurpose.providerDocument,
            file: props.file,
          });

          await createProviderDocument({
            file_upload_id: uploadReq.file_upload.id,
            document_type: values.documentType,
            description: values.description,
          });
          await props.onSuccess();
        } catch (err) {
          if (isAxiosError(err)) {
            helpers.setStatus({ type: 'error', message: err.response?.data?.error || err.message });
          } else {
            helpers.setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
          }
        }
      }}
      initialErrors={{ description: ' ' }}
      initialValues={{
        documentType: null as string | null,
        description: '',
      }}
      validationSchema={validationSchema}
    >
      {formik => {
        const hasSubmissionError = formik.status?.type === 'error';
        const submittingOrError = formik.isSubmitting || hasSubmissionError;

        return (
          <>
            <ModalTitle>{formik.isSubmitting ? 'Uploading...' : 'Add description'}</ModalTitle>
            <ModalContent noPadding>
              <Box bgcolor="grey.50" p={3}>
                <Paper variant="outlined">
                  <Box p={2} display="flex" gap="8px" alignItems="center" fontWeight={600}>
                    <Box p="3px" bgcolor="grey.300" fontSize="18px" display="flex" borderRadius="50%">
                      <InsertDriveFileOutlined htmlColor="rgba(0,0,0,0.38)" fontSize="inherit" />
                    </Box>
                    <Typography component="span" style={{ fontWeight: 600 }}>
                      {submittingOrError && formik.values.documentType
                        ? uploadedProviderDocumentTypeLabels[formik.values.documentType as UploadedProviderDocumentType]
                        : props.file.name}
                    </Typography>
                  </Box>
                  <Collapse in={formik.isSubmitting}>
                    <Box px={2} pb={2}>
                      <LinearProgress color="secondary" />
                    </Box>
                  </Collapse>
                  <Collapse in={hasSubmissionError}>
                    <Box px={2} pb={2}>
                      <AlertInline severity="error">
                        {formik.status?.message}
                      </AlertInline>
                    </Box>
                  </Collapse>
                </Paper>
              </Box>
              <Collapse in={!submittingOrError}>
                <Box p={3}>
                  <ProviderDocumentDetailsFields />
                </Box>
              </Collapse>
            </ModalContent>
            <ModalActions noPadding>
              <Collapse in={!formik.isSubmitting}>
                <Box p={2} display="flex" gap="8px">
                  <Button disabled={formik.isSubmitting} onClick={props.onCancel}>Cancel</Button>
                  <SubmitButton color="secondary">{hasSubmissionError ? 'Try again' : 'Upload'}</SubmitButton>
                </Box>
              </Collapse>
            </ModalActions>
          </>
        );
      }}
    </Formik>
  );
};


export type EditUploadedProviderDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  document: UploadedProviderDocument;
};

export const EditUploadedProviderDocumentModal: React.VFC<EditUploadedProviderDocumentModalProps> = (props) => {
  const l10n = useL10n();
  const alert = useAlert();
  const updateMutation = useUpdateUploadedProviderDocumentMutation(props.document.providerId);

  return (
    <Formik<{ documentType: UploadedProviderDocumentType; description: string }>
      onSubmit={async values => {
        try {
          await updateMutation.mutateAsync({ documentId: props.document.id, document_type: values.documentType, description: values.description });
          await props.onSuccess();
          alert.success('Document updated');
        } catch {
          alert.error(<>There was a problem updating the document. Try again or contact <strong>{l10n.helpEmail}</strong></>);
        }
      }}
      initialValues={{
        documentType: props.document.documentType,
        description: props.document.description,
      }}
      validationSchema={validationSchema}
    >
      <Modal
        open={props.open}
        onClose={props.onClose}
        title="Edit document"
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { style: { maxWidth: '600px' } } }}
      >
        <ModalContent>
          <ProviderDocumentDetailsFields />
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={props.onClose}>Cancel</CancelButton>
          <SubmitButton color="secondary">Done</SubmitButton>
        </ModalActions>
      </Modal>
    </Formik>
  );
};
